import { ReactNode } from 'react';
import { Mentor } from '../data/mentors';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { formatExpertise } from '../utils/mentorUtils';
import { languageToFlag } from '../utils/languageUtils';
import type { Components } from 'react-markdown';

interface MentorModalContentProps {
  mentor: Mentor;
  onContact?: () => void;
}

export const MentorModalContent = ({ mentor, onContact }: MentorModalContentProps) => {
  const handleContact = () => {
    const emailBody = `Dear ${mentor.name},

I would like to have an introductory call with you to discuss mentorship. I've provided some information below:

1. What specific topic or area would you like advice on?
[Please describe your main challenge or area of interest]

2. What are the biggest challenges you are facing right now?
[Brief description of your current role/project/company]

3. What are your specific goals for this mentorship?
[What do you hope to achieve through our interaction?]

4. How do you think ${mentor.name} could help you best?
[What kind of support are you looking for? (e.g., feedback, guidance, best practices)]

Looking forward to your response.

Best regards`;

    const encodedBody = encodeURIComponent(emailBody);
    window.location.href = `mailto:${mentor.email}?subject=GameMentors.be - Mentorship Request&body=${encodedBody}`;

    // Call the onContact prop if provided
    if (onContact) {
      onContact();
    }
  };

  const components: Partial<Components> = {
    h1: ({ children }: { children?: ReactNode }) => (
      <h1 className="mb-4 text-2xl font-bold">{children}</h1>
    ),
    h2: ({ children }: { children?: ReactNode }) => (
      <h2 className="mb-3 text-xl font-semibold">{children}</h2>
    ),
    h3: ({ children }: { children?: ReactNode }) => (
      <h3 className="mb-2 text-lg font-medium">{children}</h3>
    ),
    h4: ({ children }: { children?: ReactNode }) => (
      <h4 className="mb-2 text-base font-medium">{children}</h4>
    ),
    p: ({ children }: { children?: ReactNode }) => (
      <p className="mb-4 mt-2 text-gray-600 sm:mt-0">{children}</p>
    ),
    ul: ({ children }: { children?: ReactNode }) => (
      <ul className="mb-4 list-disc pl-4 text-gray-500">{children}</ul>
    ),
    ol: ({ children }: { children?: ReactNode }) => (
      <ol className="mb-4 list-decimal pl-4 text-gray-500">{children}</ol>
    ),
    li: ({ children }: { children?: ReactNode }) => (
      <li className="mb-1 text-gray-500">{children}</li>
    ),
    a: ({ children, href }: { children?: ReactNode; href?: string }) => (
      <a
        href={href}
        className="text-red-600 transition-colors duration-200 hover:text-red-700"
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),
  };

  return (
    <div className="relative h-full">
      {/* Main Content Area - with padding bottom for companies section */}
      <div className="h-full overflow-y-auto pb-4">
        <div className="py-2 sm:py-6">
          {/* Main Content Grid */}
          <div className="grid gap-4 sm:gap-8 lg:grid-cols-3">
            {/* Name and Expertise - Now at the top on mobile */}
            <div className="lg:hidden">
              <div className="mb-2">
                <h1
                  className="text-xl font-medium leading-tight text-gray-900"
                  id="modal-title-mobile"
                >
                  {mentor.name}
                </h1>
                {mentor.expertise && mentor.expertise.length > 0 && (
                  <p className="mt-2 text-sm font-medium text-gray-500">
                    {formatExpertise(mentor.expertise)}
                  </p>
                )}
              </div>
            </div>

            {/* Contact Column */}
            <div className="lg:order-2 lg:col-span-1">
              <div className="relative z-30 w-full space-y-4 bg-white sm:sticky sm:top-6 sm:px-0 lg:max-w-[280px]">
                {/* Contact Button */}
                {mentor.email && (
                  <button
                    onClick={handleContact}
                    className="flex h-12 w-full items-center justify-center gap-3 rounded-3xl border border-red-600 bg-red-600 px-6 text-sm font-medium text-white transition-colors duration-200 hover:bg-red-700"
                  >
                    Contact {mentor.name.split(' ')[0]}
                  </button>
                )}

                {/* Info List */}
                <div className="divide-y divide-gray-100">
                  {/* Location */}
                  {mentor.location && (
                    <div className="flex items-center gap-3 py-3">
                      <svg
                        className="size-5 shrink-0 text-gray-400"
                        aria-hidden="true"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-sm font-light text-gray-500">{mentor.location}</span>
                    </div>
                  )}

                  {/* Languages */}
                  {mentor.languages && mentor.languages.length > 0 && (
                    <div className="flex items-center gap-3 py-3">
                      <svg
                        className="size-5 shrink-0 text-gray-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M7 2a1 1 0 011 1v1h3a1 1 0 110 2H9.578a18.87 18.87 0 01-1.724 4.78c.29.354.596.696.914 1.026a1 1 0 11-1.44 1.389 21.034 21.034 0 01-.554-.6 19.098 19.098 0 01-3.107 3.567 1 1 0 01-1.334-1.49 17.087 17.087 0 003.13-3.733 18.992 18.992 0 01-1.487-2.494 1 1 0 111.79-.89c.234.47.489.928.764 1.372.417-.934.752-1.913.997-2.927H3a1 1 0 110-2h3V3a1 1 0 011-1zm6 6a1 1 0 01.894.553l2.991 5.982a.869.869 0 01.02.037l.99 1.98a1 1 0 11-1.79.895L15.383 16h-4.764l-.724 1.447a1 1 0 11-1.788-.894l.99-1.98.019-.038 2.99-5.982A1 1 0 0113 8zm-1.382 6h2.764L13 11.236 11.618 14z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <div className="flex gap-2">
                        {mentor.languages.map((lang) => (
                          <span key={lang} className="text-xl">
                            {languageToFlag(lang)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Stages */}
                  {mentor.stages && mentor.stages.length > 0 && (
                    <div className="flex items-center gap-3 py-3">
                      <svg
                        className="size-5 shrink-0 text-gray-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                          clipRule="evenodd"
                        />
                        <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
                      </svg>
                      <span className="text-sm text-gray-500">
                        {mentor.stages && mentor.stages.length === 3
                          ? 'For all company stages'
                          : `For ${mentor.stages
                              ?.sort((a, b) => {
                                const order = { startup: 1, growth: 2, mature: 3 };
                                return (
                                  order[a.toLowerCase() as keyof typeof order] -
                                  order[b.toLowerCase() as keyof typeof order]
                                );
                              })
                              .map((stage, index, array) => {
                                const stageText = stage.toLowerCase();
                                if (index === 0) return stageText;
                                if (index === array.length - 1) return ` and ${stageText}`;
                                return `, ${stageText}`;
                              })} companies`}
                      </span>
                    </div>
                  )}

                  {/* Price Range */}
                  {mentor.price && mentor.price.length > 0 && (
                    <div className="flex items-center gap-3 py-3">
                      <svg
                        className="size-5 shrink-0 text-gray-400"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.31-8.86c-1.77-.45-2.34-.94-2.34-1.67 0-.84.79-1.43 2.1-1.43 1.38 0 1.9.66 1.94 1.64h1.71c-.05-1.34-.87-2.57-2.49-2.97V5H10.9v1.69c-1.51.32-2.72 1.3-2.72 2.81 0 1.79 1.49 2.69 3.66 3.21 1.95.46 2.34 1.15 2.34 1.87 0 .53-.39 1.39-2.1 1.39-1.6 0-2.23-.72-2.32-1.64H8.04c.1 1.7 1.36 2.66 2.86 2.97V19h2.34v-1.67c1.52-.29 2.72-1.16 2.73-2.77-.01-2.2-1.9-2.96-3.66-3.42z" />
                      </svg>
                      <span className="text-sm text-gray-500">
                        {mentor.price.includes('Free') && mentor.price.includes('Paid')
                          ? 'Free and paid mentorship'
                          : mentor.price.includes('Free')
                            ? 'Free mentorship'
                            : 'Paid mentorship'}
                      </span>
                    </div>
                  )}

                  {/* LinkedIn */}
                  {mentor.linkedIn && (
                    <a
                      href={mentor.linkedIn}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center gap-3 py-3 text-sm font-light text-gray-500 transition-colors duration-200 hover:text-red-600"
                    >
                      <svg
                        className="ml-0.5 size-4 shrink-0 text-gray-400 transition-colors duration-200 group-hover:text-red-600"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                      </svg>
                      LinkedIn
                    </a>
                  )}

                  {/* Website */}
                  {mentor.website && (
                    <a
                      href={mentor.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center gap-3 py-3 text-sm font-light text-gray-500 transition-colors duration-200 hover:text-red-600"
                    >
                      <svg
                        className="size-5 shrink-0 text-gray-400 transition-colors duration-200 group-hover:text-red-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.083 9h1.946c.089-1.546.383-2.97.837-4.118A6.004 6.004 0 004.083 9zM10 2a8 8 0 100 16 8 8 0 000-16zm0 2c-.076 0-.232.032-.465.262-.238.234-.497.623-.737 1.182-.389.907-.673 2.142-.766 3.556h3.936c-.093-1.414-.87-2.649-.766-3.556-.24-.56-.5-.948-.737-1.182C10.232 4.032 10.076 4 10 4zm3.971 5c-.089-1.546-.383-2.97-.837-4.118A6.004 6.004 0 0115.917 9h-1.946zm-2.003 2H8.032c.093 1.414.377 2.649.766 3.556.24.56.5.948.737 1.182.233.23.389.262.465.262.076 0 .232-.032.465-.262.238-.234.498-.623.737-1.182.389-.907.673-2.142.766-3.556zm1.166 4.118c.454-1.147.748-2.572.837-4.118h1.946a6.004 6.004 0 01-2.783 4.118zm-6.268 0C6.412 13.97 6.118 12.546 6.03 11H4.083a6.004 6.004 0 002.783 4.118z"
                        />
                      </svg>
                      Visit Website
                    </a>
                  )}

                  {/* Companies Grid Section */}
                  {mentor.companies && mentor.companies.length > 0 && (
                    <div className="py-3">
                      <div className="mt-2 grid grid-cols-2 place-items-center gap-2 sm:grid-cols-2 sm:gap-4">
                        {mentor.companies.map((company, index) =>
                          company.website && company.logo ? (
                            <a
                              key={index}
                              href={company.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="group flex h-18 w-full sm:h-18"
                            >
                              <img
                                src={`/assets/companies/${company.logo}`}
                                alt={company.name}
                                className={`max-h-full w-auto max-w-[120px] object-contain sm:max-w-full ${
                                  company.className?.includes('hover:')
                                    ? company.className
                                    : company.className?.includes('invert')
                                      ? `grayscale ${company.className}`
                                      : `grayscale transition-all duration-300 group-hover:grayscale-0 ${company.className || ''}`
                                }`}
                              />
                            </a>
                          ) : company.logo ? (
                            <div key={index} className="flex h-24 w-full sm:h-18">
                              <img
                                src={`/assets/companies/${company.logo}`}
                                alt={company.name}
                                className={`max-h-full w-auto max-w-[120px] object-contain sm:max-w-full ${
                                  company.className?.includes('hover:')
                                    ? company.className
                                    : company.className?.includes('invert')
                                      ? `grayscale ${company.className}`
                                      : `grayscale transition-all duration-300 group-hover:grayscale-0 ${company.className || ''}`
                                }`}
                              />
                            </div>
                          ) : null
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Main Column */}
            <div className="lg:order-1 lg:col-span-2">
              {/* Fixed Header - Only visible on desktop */}
              <div className="mb-6 hidden lg:block">
                <div>
                  <h1
                    className="mb-2 text-2xl font-medium leading-tight text-gray-900 sm:text-3xl"
                    id="modal-title"
                  >
                    {mentor.name}
                  </h1>
                  <p className="text-sm font-medium text-gray-500">
                    {formatExpertise(mentor.expertise)}
                  </p>
                </div>
              </div>

              {/* Scrollable Description */}
              <dl className="-mt-4 text-left sm:mt-0 sm:px-0">
                {mentor.detailedDescription && (
                  <dd className="mb-8 font-light text-gray-500 sm:mb-10">
                    <ReactMarkdown
                      rehypePlugins={[rehypeRaw, rehypeSanitize]}
                      components={components}
                    >
                      {mentor.detailedDescription}
                    </ReactMarkdown>
                  </dd>
                )}
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
